"use client";

import { useState } from "react";
import {
  type ChordEntry,
  type PracticePosition,
} from "@/lib/music/chord-catalog";
import { BORDER, TEXT, TEXT_DIM, VIOLET, VIOLET_GLOW } from "./palette";
import { PEDAGOGY_PANEL_WIDTH } from "./pedagogy-panel-helpers";
import { PedagogyPanelHeader } from "./pedagogy-panel-header";
import { PedagogyPanelTheoryTab } from "./pedagogy-panel-theory-tab";
import { PedagogyPanelVariantsTab } from "./pedagogy-panel-variants-tab";
import { PedagogyPanelTipTab } from "./pedagogy-panel-tip-tab";

type Tab = "theory" | "variants" | "tip";

type Props = {
  open: boolean;
  entry: ChordEntry;
  currentPosition: PracticePosition;
  onClose: () => void;
  onPickVariant?: (variant: ChordEntry) => void;
};

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "theory", label: "Théorie" },
  { id: "variants", label: "Variantes" },
  { id: "tip", label: "Conseil" },
];

export function PedagogyPanel({
  open,
  entry,
  currentPosition,
  onClose,
  onPickVariant,
}: Props) {
  const [tab, setTab] = useState<Tab>("theory");

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: open ? PEDAGOGY_PANEL_WIDTH : 0,
        background: "linear-gradient(180deg, #0d0d12 0%, var(--color-overlay-surface) 100%)",
        borderLeft: `1px solid ${open ? VIOLET_GLOW : "transparent"}`,
        transition: "width 350ms cubic-bezier(.2,.8,.2,1), border-color 350ms",
        overflow: "hidden",
        zIndex: 50,
        boxShadow: open ? "-20px 0 60px rgba(0,0,0,0.5)" : "none",
      }}
      aria-hidden={!open}
    >
      <div
        style={{
          width: PEDAGOGY_PANEL_WIDTH,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PedagogyPanelHeader entry={entry} onClose={onClose} />

        <div
          style={{
            display: "flex",
            padding: "10px 14px 0",
            gap: 4,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          {TABS.map((t) => {
            const isActive = tab === t.id;

            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: isActive ? TEXT : TEXT_DIM,
                  borderBottom: `2px solid ${isActive ? VIOLET : "transparent"}`,
                  marginBottom: -1,
                  background: "none",
                  border: "none",
                  borderTop: "none",
                  borderLeft: "none",
                  borderRight: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 22px",
          }}
        >
          {tab === "theory" && <PedagogyPanelTheoryTab entry={entry} />}
          {tab === "variants" && (
            <PedagogyPanelVariantsTab entry={entry} onPick={onPickVariant} />
          )}
          {tab === "tip" && (
            <PedagogyPanelTipTab inversionIndex={currentPosition.inversionIndex} />
          )}
        </div>
      </div>
    </div>
  );
}

// Re-export so the parent layout can size around the panel without shift.
export { PEDAGOGY_PANEL_WIDTH };
